import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import { Link } from "react-router-dom";

const Pricing = () => {
  const plans = [
    {
      name: "Trial",
      price: "Free",
      period: "14 days",
      description: "Perfect for trying ExhibitAI",
      features: {
        "Document Upload Limit": "100 documents",
        "AI Search Queries": "50 per month",
        "Team Members": "1 user",
        "Export Features": true,
        "Email Support": true,
        "Phone Support": false,
        "Custom Integrations": false,
        "Advanced Analytics": false,
        "Dedicated Account Manager": false,
        "SLA Guarantee": false,
        "Custom Training": false,
        "API Access": false
      },
      popular: false,
      buttonText: "Start Free Trial"
    },
    {
      name: "Pro",
      price: "$99",
      period: "per month",
      description: "Ideal for small to medium law firms",
      features: {
        "Document Upload Limit": "10,000 documents",
        "AI Search Queries": "Unlimited",
        "Team Members": "Up to 10 users",
        "Export Features": true,
        "Email Support": true,
        "Phone Support": true,
        "Custom Integrations": true,
        "Advanced Analytics": true,
        "Dedicated Account Manager": false,
        "SLA Guarantee": false,
        "Custom Training": false,
        "API Access": true
      },
      popular: true,
      buttonText: "Choose Pro Plan"
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "contact us",
      description: "For large firms with complex needs",
      features: {
        "Document Upload Limit": "Unlimited",
        "AI Search Queries": "Unlimited",
        "Team Members": "Unlimited",
        "Export Features": true,
        "Email Support": true,
        "Phone Support": true,
        "Custom Integrations": true,
        "Advanced Analytics": true,
        "Dedicated Account Manager": true,
        "SLA Guarantee": true,
        "Custom Training": true,
        "API Access": true
      },
      popular: false,
      buttonText: "Contact Sales"
    }
  ];

  const allFeatures = [
    "Document Upload Limit",
    "AI Search Queries",
    "Team Members",
    "Export Features",
    "Email Support",
    "Phone Support",
    "Custom Integrations",
    "Advanced Analytics",
    "Dedicated Account Manager",
    "SLA Guarantee",
    "Custom Training",
    "API Access"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary-glow rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">E</span>
            </div>
            <span className="text-xl font-bold">ExhibitAI</span>
          </Link>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
          Choose Your Plan
        </h1>
        <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto">
          Start with our free trial and upgrade as your document search needs grow. 
          All plans include our core AI-powered search functionality.
        </p>
      </section>

      {/* Pricing Plans */}
      <section className="container mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <Card key={index} className={`relative ${plan.popular ? 'border-primary shadow-lg scale-105' : ''}`}>
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                  Most Popular
                </Badge>
              )}
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {plan.period !== "contact us" && (
                    <span className="text-muted-foreground ml-2">/{plan.period}</span>
                  )}
                </div>
                <p className="text-muted-foreground mt-2">{plan.description}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 mb-8">
                  {allFeatures.map((feature) => (
                    <div key={feature} className="flex items-center justify-between">
                      <span className="text-sm">{feature}</span>
                      <div className="flex items-center">
                        {typeof plan.features[feature] === 'boolean' ? (
                          plan.features[feature] ? (
                            <Check className="w-5 h-5 text-green-500" />
                          ) : (
                            <X className="w-5 h-5 text-muted-foreground" />
                          )
                        ) : (
                          <span className="text-sm text-muted-foreground">{plan.features[feature]}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/payment">
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-primary hover:bg-primary/90' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                  >
                    {plan.buttonText}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">
            All plans include 99.9% uptime guarantee and SOC 2 compliance
          </p>
          <div className="flex justify-center space-x-8 text-sm text-muted-foreground">
            <span>✓ No setup fees</span>
            <span>✓ Cancel anytime</span>
            <span>✓ 30-day money back guarantee</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
